<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="bg-white shadow rounded-lg mb-6">
        <div class="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Mis Órdenes</h1>
            <p class="text-sm text-gray-500">Hola, {{ userName }}</p>
          </div>
          <div class="flex space-x-3">
            <router-link :to="{ name: 'cliente-facturas' }"
              class="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100">
              Mis Facturas
            </router-link>
            <button @click="logout" class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200">
              Salir
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <svg class="animate-spin h-10 w-10 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="text-gray-600">Cargando órdenes...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p class="text-red-600 mb-3">{{ errorMsg }}</p>
        <button @click="cargarOrdenes" class="text-sm text-red-700 underline hover:text-red-800">Reintentar</button>
      </div>

      <!-- Sin órdenes -->
      <div v-else-if="ordenes.length === 0" class="bg-white shadow rounded-lg p-12 text-center">
        <p class="text-gray-500 text-lg">No tienes órdenes facturables.</p>
      </div>

      <!-- Lista de órdenes -->
      <div v-else class="space-y-4">
        <div v-for="orden in ordenes" :key="orden.id" class="bg-white shadow rounded-lg overflow-hidden">
          <div class="px-6 py-4">
            <div class="flex items-center justify-between mb-3">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">{{ orden.codigo || orden.nombre }}</h3>
                <p class="text-sm text-gray-500">{{ formatFecha(orden.created_at) }}</p>
              </div>
              <div class="text-right">
                <p class="text-xl font-bold text-gray-900">${{ formatMonto(orden.total) }}</p>
                <span :class="orden.estatus_facturacion === 'facturada' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1">
                  {{ orden.estatus_facturacion === 'facturada' ? 'Facturada' : 'Pendiente' }}
                </span>
              </div>
            </div>

            <!-- Productos -->
            <div v-if="orden.productos_cotizaciones && orden.productos_cotizaciones.length" class="border-t pt-3 mt-3">
              <p class="text-xs text-gray-500 mb-2">{{ orden.productos_cotizaciones.length }} producto(s)</p>
            </div>

            <!-- Acciones -->
            <div class="mt-3 flex justify-end">
              <button v-if="orden.estatus_facturacion === 'pendiente'"
                @click="facturar(orden)"
                class="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                Facturar
              </button>
              <span v-else class="text-sm text-gray-500 italic">Ya facturada</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import facturacionCliente from '@/Services/facturacionCliente'
import { useClienteAuthStore } from '@/stores/clienteAuth'

export default {
  name: 'ClienteOrdenes',
  data() {
    return { ordenes: [], loading: true, error: false, errorMsg: '' }
  },
  computed: {
    userName() {
      const store = useClienteAuthStore()
      return store.user?.name || 'Cliente'
    }
  },
  mounted() {
    this.checkAuth()
    this.cargarOrdenes()
  },
  methods: {
    checkAuth() {
      const store = useClienteAuthStore()
      if (!store.isAuthenticated) {
        this.$router.push({ name: 'cliente-login' })
      }
    },
    async cargarOrdenes() {
      this.loading = true
      this.error = false
      const res = await facturacionCliente.getOrdenes()
      if (res.success) {
        this.ordenes = res.data || []
      } else {
        if (res.status === 401) {
          const store = useClienteAuthStore()
          store.logout()
          this.$router.push({ name: 'cliente-login' })
          return
        }
        this.error = true
        this.errorMsg = res.message
      }
      this.loading = false
    },
    facturar(orden) {
      this.$router.push({ name: 'cliente-facturar', params: { ordenId: orden.id } })
    },
    logout() {
      const store = useClienteAuthStore()
      store.logout()
      this.$router.push({ name: 'cliente-login' })
    },
    formatFecha(f) {
      if (!f) return ''
      return new Date(f).toLocaleDateString('es-MX')
    },
    formatMonto(m) {
      if (!m) return '0.00'
      return parseFloat(m).toLocaleString('es-MX', { minimumFractionDigits: 2 })
    }
  }
}
</script>
